/**
 * AI CONTEXT: component - Notification Settings for existing users
 * Journal: J-20250103-01
 * 
 * Purpose:
 * - Allow existing users to manage push notification preferences
 * - Provide opt-in/opt-out controls for different notification types
 * - Integrate with existing SMS and email notification settings
 * - Show current notification status and device information
 * 
 * Features:
 * - Push notification consent management
 * - Device token management
 * - Notification type preferences
 * - Test notification functionality
 * - Privacy and compliance information
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Smartphone, Mail, MessageSquare, Settings, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import pushService from '../services/pushService';
import { getUserPhoneE164, canReceiveSms } from '../services/smsService';
import { getMyProfile, updateMyProfile } from '../services/apiClient';

const NotificationSettings = () => {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [settings, setSettings] = useState({
    pushEnabled: false,
    smsEnabled: false,
    emailEnabled: true,
    giveawayAlerts: true,
    orderUpdates: true,
    promotionalOffers: false,
    cartReminders: true
  });
  const [devices, setDevices] = useState([]);
  const [browserSupport, setBrowserSupport] = useState({
    pushSupported: false,
    permission: 'default'
  });

  const checkBrowserSupport = async () => {
    const supported = pushService.isSupported;
    let permission = 'denied';
    
    if (supported && 'Notification' in window) {
      permission = Notification.permission;
    }
    
    setBrowserSupport({
      pushSupported: supported,
      permission: permission
    });
  };

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      
      // Push notifications disabled - skip loading
      
      // Load SMS preferences from user profile in DynamoDB
      try {
        const profile = await getMyProfile();
        const smsEnabled = profile?.smsConsent || false;
        setSettings(prev => ({ ...prev, smsEnabled }));
      } catch (error) {
        console.warn('Failed to load SMS preferences from profile, falling back to Cognito attributes:', error);
        // Fallback to Cognito attributes if profile loading fails
        const smsEnabled = canReceiveSms(user);
        setSettings(prev => ({ ...prev, smsEnabled }));
      }
      
      } catch (error) {
        console.error('Error loading notification settings:', error);
      } finally {
        setLoading(false);
      }
    }, [user]);

  useEffect(() => {
    loadSettings();
    // checkBrowserSupport(); // Disabled - push notifications not in use
  }, [loadSettings]);

  const handlePushToggle = async () => {
    try {
      setLoading(true);
      
      if (!settings.pushEnabled) {
        // Enable push notifications
        const hasPermission = await pushService.requestPermission();
        if (hasPermission) {
          await pushService.subscribe();
          setSettings(prev => ({ ...prev, pushEnabled: true }));
        } else {
          throw new Error('Permission denied');
        }
      } else {
        // Disable push notifications
        await pushService.unsubscribe();
        setSettings(prev => ({ ...prev, pushEnabled: false }));
      }
      
      // Update preferences
      await pushService.updatePreferences({
        optIn: !settings.pushEnabled,
        giveawayAlerts: settings.giveawayAlerts,
        orderUpdates: settings.orderUpdates,
        promotionalOffers: settings.promotionalOffers,
        cartReminders: settings.cartReminders
      });
      
    } catch (error) {
      console.error('Error toggling push notifications:', error);
      alert('Failed to update push notification settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSmsToggle = async () => {
    try {
      setLoading(true);

      const nextEnabled = !settings.smsEnabled;
      const phoneE164 = getUserPhoneE164(user);
      if (!phoneE164) {
        throw new Error('No phone number on file. Add your phone in profile.');
      }

      // Update SMS consent in user profile
      await updateMyProfile({
        smsConsent: nextEnabled
      });

      setSettings(prev => ({ ...prev, smsEnabled: nextEnabled }));
    } catch (error) {
      console.error('Error toggling SMS notifications:', error);
      alert('Failed to update SMS settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    try {
      setSettings(prev => ({ ...prev, [key]: value }));
      
      // Update preferences if push is enabled
      if (settings.pushEnabled) {
        await pushService.updatePreferences({
          optIn: settings.pushEnabled,
          giveawayAlerts: key === 'giveawayAlerts' ? value : settings.giveawayAlerts,
          orderUpdates: key === 'orderUpdates' ? value : settings.orderUpdates,
          promotionalOffers: key === 'promotionalOffers' ? value : settings.promotionalOffers,
          cartReminders: key === 'cartReminders' ? value : settings.cartReminders
        });
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      // Revert the change
      setSettings(prev => ({ ...prev, [key]: !value }));
    }
  };

  const handleTestNotification = async () => {
    try {
      setTestLoading(true);
      await pushService.sendTestNotification();
      alert('Test notification sent! Check your device.');
    } catch (error) {
      console.error('Error sending test notification:', error);
      alert('Failed to send test notification. Please check your settings.');
    } finally {
      setTestLoading(false);
    }
  };

  const getPermissionStatus = () => {
    if (!browserSupport.pushSupported) {
      return { status: 'unsupported', message: 'Push notifications not supported in this browser' };
    }
    
    switch (browserSupport.permission) {
      case 'granted':
        return { status: 'granted', message: 'Push notifications are enabled' };
      case 'denied':
        return { status: 'denied', message: 'Push notifications are blocked. Please enable them in your browser settings.' };
      default:
        return { status: 'default', message: 'Push notifications permission not requested' };
    }
  };

  const permissionStatus = getPermissionStatus();

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <Bell className="h-8 w-8 text-custom-orange" />
          <h1 className="text-3xl font-bold text-theme-silver">Notification Settings</h1>
        </div>
        <p className="text-theme-silver-light">
          Manage how you receive notifications from Tolbert Tactical
        </p>
      </div>

      {/* Push Notifications Section - DISABLED */}
      {/* Connected Devices */}
      {devices.length > 0 && (
        <div className="mt-6">
          <h4 className="text-theme-silver font-medium mb-3">Connected Devices</h4>
          <div className="space-y-2">
            {devices.map((device, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-theme-dark/30 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Smartphone className="h-4 w-4 text-theme-silver-light" />
                  <div>
                    <p className="text-theme-silver text-sm">
                      {device.channelType === 'WEB_PUSH' ? 'Web Browser' : device.channelType}
                    </p>
                    <p className="text-theme-silver-light text-xs">
                      {device.enabled ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                </div>
                <div className={`w-2 h-2 rounded-full ${device.enabled ? 'bg-green-500' : 'bg-red-500'}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SMS Notifications Section */}
      <div className="bg-theme-dark/50 rounded-lg p-6 border border-theme-silver/20">
        <div className="flex items-center space-x-3 mb-6">
          <MessageSquare className="h-6 w-6 text-custom-orange" />
          <h2 className="text-xl font-semibold text-theme-silver">SMS Notifications</h2>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-theme-silver">SMS Alerts</h3>
            <p className="text-theme-silver-light text-sm">
              Receive text messages for important updates and giveaways
            </p>
          </div>
          <button
            onClick={handleSmsToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.smsEnabled ? 'bg-custom-orange' : 'bg-theme-silver/30'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.smsEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Email Notifications Section */}
      <div className="bg-theme-dark/50 rounded-lg p-6 border border-theme-silver/20">
        <div className="flex items-center space-x-3 mb-6">
          <Mail className="h-6 w-6 text-custom-orange" />
          <h2 className="text-xl font-semibold text-theme-silver">Email Notifications</h2>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-theme-silver">Email Updates</h3>
            <p className="text-theme-silver-light text-sm">
              Receive detailed notifications and updates via email
            </p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, emailEnabled: !prev.emailEnabled }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.emailEnabled ? 'bg-custom-orange' : 'bg-theme-silver/30'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.emailEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Privacy Information */}
      <div className="bg-theme-dark/30 rounded-lg p-6 border border-theme-silver/10">
        <div className="flex items-center space-x-3 mb-4">
          <Settings className="h-5 w-5 text-theme-silver-light" />
          <h3 className="text-lg font-medium text-theme-silver">Privacy & Control</h3>
        </div>
        <div className="text-theme-silver-light text-sm space-y-2">
          <p>• You can change these settings at any time</p>
          <p>• We respect your privacy and only send relevant notifications</p>
          <p>• You can opt out of specific notification types while keeping others</p>
          <p>• All notification data is encrypted and securely stored</p>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
